// IAM & RBAC types — ADR-033: Fine-grained permission model

// ── Permission Actions (discrete flags, not hierarchical) ──

/** Permission action — each is an independent boolean grant */
export type PermissionAction = 'read' | 'write' | 'delete' | 'execute' | 'admin'

export type AccessLevel = 'none' | 'read' | 'write' | 'admin'

// ── User Classes ──

/** User class categories — determines auth provider and base capabilities */
export type UserClass = 'internal' | 'external' | 'customer' | 'supplier' | 'applicant' | 'ai_agent'

// ── Roles ──

/** A role from the user_roles table */
export interface IAMRole {
  id: string
  label: string
  userClass: UserClass
  isSystem: boolean
  sortOrder: number
  createdAt: string
}

// ── App & Feature Registry (data-driven navigation) ──

/** An app from app_registry — source of truth for navigation */
export interface AppRegistryEntry {
  id: string
  label: string
  icon: string | null
  href: string | null
  sortOrder: number
  isActive: boolean
  tenantId: string | null
}

/** A feature from feature_registry — sub-app navigation item */
export interface FeatureRegistryEntry {
  id: string
  appId: string
  label: string
  icon: string | null
  href: string | null
  sortOrder: number
  isActive: boolean
}

// ── Permissions ──

/** A single permission grant (from role_permissions or user_permissions) */
export interface PermissionGrant {
  appId: string
  featureId: string | null
  action: PermissionAction
  granted: boolean
  source: 'user' | 'role'
  validUntil: string | null
  maxInvocations: number | null
  currentInvocations: number | null
}

/** The resolved permission map for a user — returned by get_user_permission_map */
export type PermissionMap = PermissionGrant[]

/** Check if a permission map grants a specific action */
export function hasPermission(
  map: PermissionMap,
  appId: string,
  action: PermissionAction = 'read',
  featureId?: string | null,
): boolean {
  // 1. Exact match (app + feature + action)
  if (featureId) {
    const exact = map.find(p => p.appId === appId && p.featureId === featureId && p.action === action)
    if (exact) return exact.granted
  }

  // 2. App-level match (app + null feature + action)
  const appLevel = map.find(p => p.appId === appId && p.featureId === null && p.action === action)
  if (appLevel) return appLevel.granted

  return false
}

/** Check if user has any access to an app (any action granted) */
export function hasAppAccess(map: PermissionMap, appId: string): boolean {
  return map.some(p => p.appId === appId && p.granted)
}

/** Get all granted features for an app */
export function getGrantedFeatures(map: PermissionMap, appId: string, action: PermissionAction = 'read'): string[] {
  return map
    .filter(p => p.appId === appId && p.featureId !== null && p.action === action && p.granted)
    .map(p => p.featureId!)
}

// ── Access Levels (Layer 2 — Odoo pattern) ──

/** Access level key — universal levels seeded for every app */
export type AccessLevelKey = 'none' | 'read_only' | 'user' | 'manager' | 'full' | string

/** An access level definition from app_access_levels */
export interface AppAccessLevel {
  id: string
  appId: string
  levelKey: AccessLevelKey
  label: string
  description: string | null
  sortOrder: number
  ruleCount: number
}

/** A role's assigned access level per app from role_app_levels */
export interface RoleAppLevel {
  roleId: string
  appId: string
  levelKey: AccessLevelKey
  label: string
}

/** Payload for setting role access levels (Odoo-style dropdown) */
export interface SetRoleLevelsPayload {
  roleId: string
  levels: Array<{
    appId: string
    levelKey: AccessLevelKey
  }>
}

// ── Role Permissions (defaults) ──

/** Role permission entry from role_permissions */
export interface RolePermission {
  roleId: string
  appId: string
  featureId: string | null
  action: PermissionAction
  granted: boolean
  validFrom: string | null
  validUntil: string | null
  maxInvocations: number | null
  rateLimitPerHour: number | null
}

// ── User Permissions (overrides) ──

/** User permission entry from user_permissions */
export interface UserPermission {
  userId: string
  appId: string
  featureId: string | null
  action: PermissionAction
  granted: boolean
  validFrom: string | null
  validUntil: string | null
  maxInvocations: number | null
  currentInvocations: number
  rateLimitPerHour: number | null
  grantedBy: string | null
  reason: string | null
}

/** Payload for setting user permissions */
export interface SetUserPermissionsPayload {
  userId: string
  permissions: Array<{
    appId: string
    featureId?: string | null
    action: PermissionAction
    granted: boolean
    validFrom?: string | null
    validUntil?: string | null
    maxInvocations?: number | null
    rateLimitPerHour?: number | null
    reason?: string | null
  }>
}

/** Payload for setting role permissions */
export interface SetRolePermissionsPayload {
  roleId: string
  permissions: Array<{
    appId: string
    featureId?: string | null
    action: PermissionAction
    granted: boolean
  }>
}

